import type { RefObject } from 'react';

import { useEffect } from 'react';

import { rewriteEditorAnchorHrefIfDocumentPath } from '../editor-document-links';

type NavigateFn = (to: string) => void;

export function useEditorHolderLinkNavigation(
  holderRef: RefObject<HTMLDivElement | null>,
  {
    documentPathRegex,
    navigate,
  }: {
    documentPathRegex: string;
    navigate: NavigateFn;
  },
): void {
  // Plain click on <a href>: Editor.js does not follow links without Ctrl/Cmd.
  // `rewriteEditorAnchorHrefIfDocumentPath` only rewrites short document paths;
  // it returns null for reserved prefixes like `/d/...` and legacy `/documents/...`, so we still
  // navigate those hrefs here. Same-origin `/api/...` links are left to the
  // browser so uploads and other API URLs are not routed as app pages.
  useEffect(() => {
    const el = holderRef.current;
    if (!el) return;

    const origin = window.location.origin;
    const onClickCapture = (ev: MouseEvent) => {
      if (ev.defaultPrevented) return;

      if (ev.button !== 0) return;

      if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;

      const t = ev.target;
      if (!(t instanceof Element)) return;

      const anchor = t.closest('a');
      if (!anchor || !el.contains(anchor)) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      const trimmed = href.trim();
      if (trimmed.toLowerCase().startsWith('javascript:')) return;

      const shortDocDest = rewriteEditorAnchorHrefIfDocumentPath(
        href,
        documentPathRegex,
        origin,
      );
      if (shortDocDest) {
        ev.preventDefault();
        ev.stopPropagation();
        navigate(shortDocDest);
        return;
      }

      try {
        if (/^mailto:/i.test(trimmed) || /^tel:/i.test(trimmed)) {
          ev.preventDefault();
          ev.stopPropagation();
          window.location.assign(trimmed);
          return;
        }

        if (/^https?:\/\//i.test(trimmed)) {
          const u = new URL(trimmed);
          if (u.origin === origin) {
            // API routes (uploads, GraphQL, etc.) are not SPA pages; use full navigation.
            if (u.pathname === '/api' || u.pathname.startsWith('/api/')) {
              return;
            }

            ev.preventDefault();
            ev.stopPropagation();
            navigate(`${u.pathname}${u.search}${u.hash}`);
          } else {
            ev.preventDefault();
            ev.stopPropagation();
            window.open(u.href, '_blank', 'noopener,noreferrer');
          }

          return;
        }

        if (trimmed.startsWith('/')) {
          const u = new URL(trimmed, `${origin}/`);
          if (u.origin === origin) {
            if (u.pathname === '/api' || u.pathname.startsWith('/api/')) {
              return;
            }

            ev.preventDefault();
            ev.stopPropagation();
            navigate(`${u.pathname}${u.search}${u.hash}`);
          }
        }
      } catch {
        /* ignore invalid href */
      }
    };
    el.addEventListener('click', onClickCapture, true);
    return () => el.removeEventListener('click', onClickCapture, true);
  }, [documentPathRegex, navigate]);
}
